import { useState, useEffect, useRef } from "react";
import styles from "../../../pages/ordermanager/Orders.module.css";
import { useToast } from "../Toaster/Toaster";
import { RemoveIcon } from "../../icons/Icon";

const CustomDropdown = ({
  value,
  options,
  placeholder = "Select",
  onChange,
  onEnter,
  dropdownRef,
  mode,
  open,
  onOpen,
  onClose,
}) => {
  const containerRef = useRef(null);
  const [highlighted, setHighlighted] = useState(-1);

  useEffect(() => {
    const handleOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        onClose();
      }
    };
    if (open) document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      const idx = options.findIndex(
        (o) => (o.value ?? o.warehousename) === value,
      );
      setHighlighted(idx >= 0 ? idx : 0);
    }
  }, [open]);

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        onOpen();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlighted >= 0 && highlighted < options.length) {
        const opt = options[highlighted];
        const selectedVal = opt.value ?? opt.warehousename;
        onChange(selectedVal, opt);
        onClose();
        if (onEnter) onEnter(selectedVal, opt);
      }
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  const displayLabel = value
    ? (options.find((o) => (o.value ?? o.warehousename) === value)?.label ??
      options.find((o) => (o.value ?? o.warehousename) === value)
        ?.warehousename ??
      value)
    : null;

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
      <div
        ref={dropdownRef}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onClick={() => (open ? onClose() : onOpen())}
        className={`${styles.item_inp} ${mode ? styles.item_inp_dark : ""}`}
        style={{
          display: "flex",
          alignItems: "center",
          // justifyContent: "space-between",
          cursor: "pointer",
          userSelect: "none",
          paddingRight: "8px",
          boxSizing: "border-box",
          gap: "10px",
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: displayLabel ? "inherit" : "#757575",
            fontSize: "0.82rem",
          }}
        >
          {displayLabel ?? placeholder}
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          style={{
            flexShrink: 0,
            marginLeft: 4,
            transition: "transform 0.18s",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            opacity: 0.5,
          }}
        >
          <path
            d="M2 4L6 8L10 4"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            zIndex: 9999,
            minWidth: "100%",
            maxHeight: "220px",
            overflowY: "auto",
            background: mode ? "#232529" : "#ffffff",
            border: mode ? "1px solid #1B1D21" : "1px solid #e2e8f0",
            borderRadius: "10px",
            boxShadow: mode
              ? "0 8px 32px rgba(0,0,0,0.45)"
              : "0 8px 32px rgba(0,0,0,0.12)",
            padding: "6px",
            animation: "dropdownFadeIn 0.14s ease",
          }}
        >
          {options.length === 0 && (
            <div
              style={{
                padding: "10px 12px",
                fontSize: "0.8rem",
                color: "#757575",
                textAlign: "center",
              }}
            >
              No options
            </div>
          )}
          {options.map((opt, i) => {
            const optValue = opt.value ?? opt.warehousename;
            const optLabel = opt.label ?? opt.warehousename;
            const isSelected = optValue === value;
            const isHigh = i === highlighted;
            return (
              <div
                key={opt._id ?? opt.value ?? i}
                onMouseEnter={() => setHighlighted(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(optValue, opt);
                  onClose();
                  if (onEnter) onEnter(optValue, opt);
                }}
                style={{
                  padding: "8px 12px",
                  borderRadius: "7px",
                  fontSize: "0.82rem",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontWeight: isSelected ? 600 : 400,
                  background: isHigh
                    ? mode
                      ? "#333333"
                      : "#f0f4ff"
                    : "transparent",
                  color: isSelected
                    ? mode
                      ? "#ffffff"
                      : "#3b5bdb"
                    : mode
                      ? "#ffffff"
                      : "#1e293b",
                  transition: "background 0.1s",
                }}
              >
                {optLabel}
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        @keyframes dropdownFadeIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

const DirectCollectItemList = ({
  mode,
  index,
  item,
  itemInpRefs,
  onItemChange,
  onRemove,
  itemDetailsLength,
  onAddNewItem,
  quantityRefs,
  setShowItemSearch,
  itemFocus,
  itemBlur,
  focusedItemIndex,
  showItemSearch,
  onShowDesc,
  onCompletion,
  flag,
  AllWarehouse,
  units,
}) => {
  const { show } = useToast();

  const descInpRef = useRef(null);
  const inputRef = useRef(null);
  const warehouseDropdownRef = useRef(null);
  const unitDropdownRef = useRef(null);
  const priceRefs = useRef([]);

  const [showDescInput, setShowDescInput] = useState(false);
  const [description, setDescription] = useState(item.desc || "");
  const [selectedItem, setSelectedItem] = useState(null);
  const [isEdit, setIsEdit] = useState(false);

  // Dropdown open states
  const [warehouseOpen, setWarehouseOpen] = useState(false);
  const [unitOpen, setUnitOpen] = useState(false);

  // console.log(item);

  /* ── Item No handlers ── */
  const handleInputChange = (e) => {
    const value = e.target.value;
    if (value?.length < item?.itemNo?.length) {
      setSelectedItem(null);
    }
    onItemChange("itemNo", value);
    setShowItemSearch(value?.length > 2);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();

      // If itemNo is empty — remove row or warn
      if (!e.target.value || e.target.value.trim() === "") {
        if (itemDetailsLength > 1) {
          onCompletion && onCompletion();
          onRemove(index);
        } else {
          show(
            "YOU CANNOT ADD NEW ITEM BEFORE FILLING THE CURRENT ONE!",
            "warning",
          );
        }
        return;
      }

      // itemNo has value — open warehouse dropdown
      setWarehouseOpen(true);
      setTimeout(() => {
        warehouseDropdownRef.current?.focus();
      }, 50);
    }
  };

  /* ── Warehouse handlers ── */
  const handleWarehouseChange = (val, opt) => {
    // console.log(val, opt);

    onItemChange("warehouse", opt?.label ?? "");
    onItemChange("warehouseId", opt?.value ?? "");
  };

  const handleWarehouseEnter = (selectedVal, opt) => {
    const val = selectedVal ?? item.warehouse;
    if (!val) {
      show("Select warehouse", "warning");
      return;
    }
    setTimeout(() => {
      if (quantityRefs.current) {
        quantityRefs.current[index]?.focus();
      }
    }, 50);
  };

  /* ── Quantity handlers ── */
  const handleQuantityChange = (e) => {
    const qty = Number(e.target.value);
    onItemChange("qty", qty);
    if (item.price) {
      onItemChange("amount", qty * item.price);
    }
  };

  const validateQuantity = (selectedItem, qty) => {
    const i = isEdit ? item : selectedItem;

    const warnings = [];
    const isOutsourced = i?.outsourcedItem;

    if (!qty) warnings.push(`Quantity cannot be empty`);

    if (isOutsourced === "Disable" && Number(qty) > Number(i.totalQty)) {
      warnings.push(`Quantity exceeds available stock`);
    }
    if (isOutsourced === "Active" && qty > i.outSourceItemlimit) {
      warnings.push(`Quantity exceeds limit out source limit`);
    }

    if (warnings?.length > 0) {
      show(warnings, "warning");
      return false;
    }
    return true;
  };

  const handleQuantityKeyDown = (e) => {
    if (e.key === "-" || e.key === "e") {
      e.preventDefault();
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (validateQuantity(selectedItem, e.target.value)) {
        setUnitOpen(true);
        setTimeout(() => {
          unitDropdownRef.current?.focus();
        }, 50);
      }
    }
  };

  /* ── Unit handlers ── */
  const handleUnitChange = (val) => {
    onItemChange("unit", val);
  };

  const handleUnitEnter = (selectedVal) => {
    const val = selectedVal ?? item.unit;
    if (!val) {
      show("Select unit", "warning");
      return;
    }
    setTimeout(() => {
      priceRefs.current[index]?.focus();
    }, 50);
  };

  /* ── Price handlers ── */
  const handlePriceKeyDown = (e) => {
    if (e.key === "-" || e.key === "e" || e.key === "+" || e.key === "E") {
      e.preventDefault();
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const price = Number(e.target.value);
      if (!price || price <= 0) {
        show("Enter valid price greater than 0", "warning");
        return;
      }
      descToggle(true);
    }
  };

  /* ── Desc handlers ── */
  const descToggle = (value) => {
    setShowDescInput(value);
    if (onShowDesc) onShowDesc(value);
  };

  const handleDescChange = (e) => {
    setDescription(e.target.value);
  };

  const handleDescKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onItemChange("desc", description);
      descToggle(false);

      if (index === itemDetailsLength - 1) {
        onAddNewItem();
      } else {
        setShowItemSearch(false);
        setTimeout(() => {
          const nextInput = document.querySelector(
            `input[data-item-index="${index + 1}"]`,
          );
          if (nextInput) nextInput.focus();
        }, 50);
      }
    }
  };

  /* ── Remove item ── */
  const handleItemKeyDown = (e) => {
    if (e.ctrlKey && e.key === "Backspace") {
      e.preventDefault();
      if (itemDetailsLength === 1) {
        show("YOU CANNOT REMOVE THE ONLY ITEM!", "warning");
        return;
      }
      onRemove(index);
      setTimeout(() => {
        if (index > 0) {
          const prevQuantityRef = quantityRefs.current[index - 1];
          if (prevQuantityRef) prevQuantityRef.focus();
        }
      }, 50);
    }
  };

  /* ── Effects ── */
  useEffect(() => {
    if (inputRef.current) itemInpRefs(inputRef.current);
  }, [itemInpRefs]);

  useEffect(() => {
    if (showDescInput && descInpRef.current) descInpRef.current.focus();
  }, [showDescInput]);

  useEffect(() => {
    const allValuesZeroOrEmpty = Object.values(item).every(
      (value) => value === 0 || value === "",
    );
    setIsEdit(!allValuesZeroOrEmpty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (flag === "create") setDescription("");
  }, [flag]);

  return (
    <div
      style={{ display: "flex", flexDirection: "column" }}
      onKeyDown={handleItemKeyDown}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <li
          className={`${styles.p_table_row} ${
            mode ? styles.p_table_row_dark : ""
          }`}
        >
          {/* S.No */}
          <div className={styles.entry_2}>{index + 1}</div>

          {/* Item No */}
          <div className={styles.entry_2} style={{ overflow: "visible" }}>
            <div className={styles.inp_div} style={{ position: "relative" }}>
              <input
                ref={inputRef}
                data-item-index={index}
                type="text"
                placeholder="Item No"
                autoComplete={"new-password"}
                className={`${styles.item_inp} ${
                  mode ? styles.item_inp_dark : ""
                }`}
                value={item?.itemNo?.toUpperCase() || ""}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => itemFocus(index)}
                onBlur={itemBlur}
              />
            </div>
          </div>

          {/* <div className={styles.entry_2}>{item.product_name || "-"}</div>
          <div className={styles.entry_2}>{item.collection_name || "-"}</div> */}


          {/* Warehouse – Custom Dropdown */}
          <div className={styles.entry_2} style={{ overflow: "visible" }}>
            <CustomDropdown
              value={item.warehouse || ""}
              options={AllWarehouse || []}
              placeholder="Warehouse"
              onChange={handleWarehouseChange}
              onEnter={handleWarehouseEnter}
              dropdownRef={warehouseDropdownRef}
              mode={mode}
              open={warehouseOpen}
              onOpen={() => setWarehouseOpen(true)}
              onClose={() => setWarehouseOpen(false)}
            />
          </div>

          {/* Quantity */}
          <div className={styles.entry_2}>
            <input
              ref={(el) => {
                if (el && quantityRefs.current) {
                  quantityRefs.current[index] = el;
                }
              }}
              type="number"
              min="0"
              step="1"
              placeholder="Qty"
              autoComplete={"new-password"}
              value={item.qty || ""}
              onChange={handleQuantityChange}
              onKeyDown={handleQuantityKeyDown}
              className={`${styles.item_inp} ${
                mode ? styles.item_inp_dark : {}
              }`}
            />
          </div>

          {/* Unit – Custom Dropdown */}
          <div className={styles.entry_2} style={{ overflow: "visible" }}>
            <CustomDropdown
              value={item.unit || ""}
              options={units || []}
              placeholder="Unit"
              onChange={handleUnitChange}
              onEnter={handleUnitEnter}
              dropdownRef={unitDropdownRef}
              mode={mode}
              open={unitOpen}
              onOpen={() => setUnitOpen(true)}
              onClose={() => setUnitOpen(false)}
            />
          </div>

          {/* Price */}
          <div className={styles.entry_2}>
            <input
              ref={(el) => {
                if (el && priceRefs.current) {
                  priceRefs.current[index] = el;
                }
              }}
              type="text"
              value={item.price ?? ""}
              onBlur={() => {
                if (item.price) {
                  onItemChange("price", Number(item.price).toFixed(2));
                }
              }}
              onChange={(e) => {
                let value = e.target.value;
                if (!/^\d*\.?\d*$/.test(value)) return;
                onItemChange("price", value);
                if (item.qty) {
                  onItemChange("amount", Number(value) * item.qty);
                }
              }}
              onKeyDown={handlePriceKeyDown}
              className={`${styles.item_inp} ${
                mode ? styles.item_inp_dark : {}
              }`}
            />
          </div>

          {/* Amount */}
          <div className={styles.entry_2}>
            {flag === "create"
              ? item?.amount?.toFixed(2)
              : item?.price * item?.qty}
          </div>
        </li>

        {/* Remove button */}
        {itemDetailsLength > 1 && (
          <div style={{ cursor: "pointer" }} onClick={onRemove}>
            <RemoveIcon />
          </div>
        )}
      </div>

      {/* Description input */}
      {(showDescInput || item?.desc?.length !== 0) && (
        <input
          type="text"
          placeholder="Enter description"
          value={description}
          onChange={handleDescChange}
          onKeyDown={handleDescKeyDown}
          onBlur={() => {
            onItemChange("desc", description);
            setShowDescInput(false);
          }}
          className={`${styles.item_inp} ${styles.desc} ${
            mode ? styles.item_inp_dark : ""
          }`}
          ref={descInpRef}
        />
      )}
    </div>
  );
};

export default DirectCollectItemList;
